// contador-contexto.interceptor.ts
// Interceptor de SEGURANCA da feature "Operar Cliente" (Area do Contador).
//
// Injeta o header `X-Empresa-Contexto: {clienteEmpresaId}` SOMENTE quando:
//   1) ha um cliente ativo no ContadorContextoService (estado != null); E
//   2) o par (metodo, path) da requisicao casa a allowlist do FRONT, que espelha a
//      `ROTAS_CONTEXTO` do backend (`shared/contexto-rotas.ts`, com teste de paridade).
//
// A allowlist e de PARES, nao de caminhos: `/nota/` liberado significa `GET /nota/`. Foi por
// casar so o caminho (com `includes`/`endsWith`) que este interceptor injetava o header em
// `PUT /nota/cancelar/{id}` — e o backend, que so honra `GET /nota/`, descartava o header e
// cancelava em cascata a nota do CONTADOR com 200 OK. Desde o AP-10 o backend responde 400
// nesses casos em vez de ignorar em silencio; casar por par e o que evita o 400 legitimo.
//
// Sem cliente ativo => requisicao intocada (zero vazamento). Usa setHeaders
// (merge) — nunca sobrescreve Authorization/Accept-Language do AuthInterceptor.
//
// Registro em app.module.ts: Auth -> ContadorContexto -> HttpError.

import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpContextToken,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { ContadorContextoService } from '../services/contador/contador-contexto.service';
import { casaAllowlist, ROTAS_CONTEXTO_CONTADOR } from './contexto-rotas';

/**
 * Opt-out explicito, por chamada, do contexto de cliente ativo.
 *
 * Quando uma request e criada com `context: new HttpContext().set(
 * SEM_CONTEXTO_CONTADOR, true)`, este interceptor a deixa INTOCADA — nao injeta
 * `X-Empresa-Contexto` mesmo que exista cliente ativo e a URL esteja na
 * allowlist. Usar quando a tela precisa dos dados da PROPRIA empresa do contador
 * (ex.: contas do contador para forma de pagamento) numa rota que, por padrao,
 * seria sequestrada pelo contexto do cliente (ex.: `/api/conta`).
 */
export const SEM_CONTEXTO_CONTADOR = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ContadorContextoInterceptor implements HttpInterceptor {
  constructor(private contexto: ContadorContextoService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Opt-out explicito por chamada: a request pediu para NAO herdar o contexto
    // do cliente ativo (ex.: contas da propria empresa do contador). Deixa
    // intocada antes mesmo de olhar cliente/allowlist.
    if (req.context.get(SEM_CONTEXTO_CONTADOR)) {
      return next.handle(req);
    }

    const cliente = this.contexto.clienteAtual();

    // So age se HA cliente ativo E o par (metodo, path) casa a allowlist do front.
    if (cliente && casaAllowlist(ROTAS_CONTEXTO_CONTADOR, req.method, req.url)) {
      req = req.clone({
        setHeaders: { 'X-Empresa-Contexto': String(cliente.clienteEmpresaId) },
      });
    }

    return next.handle(req);
  }
}
